/**
 * QuotaProgress Component
 *
 * A reusable progress bar for displaying quota usage with multiple quota types.
 * Supports all granularities: second, minute, hour, day, week, month, tpm.
 */

import React from 'react';

export interface QuotaValue {
  used: number;
  total: number | false;
  reset_at?: string;
}

export interface QuotaProgressProps {
  quota: QuotaValue;
  label: string;
  showResetTime?: boolean;
  size?: 'sm' | 'md' | 'lg';
  warningThreshold?: number;
  criticalThreshold?: number;
  className?: string;
}

const sizeClasses = {
  sm: 'h-1.5',
  md: 'h-2.5',
  lg: 'h-4',
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};

const formatResetTime = (resetAt: string | undefined): string => {
  if (!resetAt) return '';
  try {
    const date = new Date(resetAt);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    if (diffMs <= 0) return '已重置';
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}分钟后重置`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}小时后重置`;
    return `${Math.floor(diffHours / 24)}天后重置`;
  } catch {
    return '';
  }
};

export const QuotaProgress: React.FC<QuotaProgressProps> = ({
  quota,
  label,
  showResetTime = true,
  size = 'md',
  warningThreshold = 80,
  criticalThreshold = 95,
  className = '',
}) => {
  const { used, total, reset_at } = quota;

  if (total === false) {
    return (
      <div className={`flex items-center justify-between ${className}`}>
        <span className="text-sm text-gray-600">{label}</span>
        <span className="text-sm font-medium text-gray-500">
          {formatNumber(used)} (无限制)
        </span>
      </div>
    );
  }

  const percentage = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const remaining = Math.max(total - used, 0);

  let barColor = 'bg-green-500';
  let textColor = 'text-green-600';
  if (percentage >= criticalThreshold) {
    barColor = 'bg-red-500';
    textColor = 'text-red-600';
  } else if (percentage >= warningThreshold) {
    barColor = 'bg-yellow-500';
    textColor = 'text-yellow-600';
  }

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">{label}</span>
        <span className={`text-sm font-medium ${textColor}`}>
          {formatNumber(used)} / {formatNumber(total)}
        </span>
      </div>
      <div className={`w-full bg-gray-200 rounded-full overflow-hidden ${sizeClasses[size]}`}>
        <div
          className={`${barColor} rounded-full transition-all duration-150`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>
          剩余 <span className={textColor}>{formatNumber(remaining)}</span>
        </span>
        {showResetTime && reset_at && (
          <span>{formatResetTime(reset_at)}</span>
        )}
      </div>
    </div>
  );
};

export default QuotaProgress;